"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Send, Loader, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Toaster } from "@/components/ui/sonner";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center p-8 space-y-4 bg-primary/10 border border-primary/20 rounded-sm animate-in fade-in zoom-in duration-300">
        <div className="size-16 bg-primary/20 text-primary rounded-full flex items-center justify-center mb-2 shadow-lg">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-xl font-serif font-bold tracking-tight text-primary uppercase">Inquiry Received</h3>
        <p className="text-muted-foreground font-sans max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary-foreground transition-all duration-300 rounded-sm">
          Submit New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-widest">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Alexander Pierce" {...field} className="bg-secondary/40 border-border/50 focus:ring-primary focus:border-primary rounded-sm placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage className="text-destructive font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-widest">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="alex@luxury.com" {...field} className="bg-secondary/40 border-border/50 focus:ring-primary focus:border-primary rounded-sm placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage className="text-destructive font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-widest">Phone <span className="text-muted-foreground font-normal text-[10px] ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="bg-secondary/40 border-border/50 focus:ring-primary focus:border-primary rounded-sm placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage className="text-destructive font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono text-xs uppercase tracking-widest">Vehicle Make & Model <span className="text-muted-foreground font-normal text-[10px] ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input placeholder="e.g. 2024 Porsche 911 GT3" {...field} className="bg-secondary/40 border-border/50 focus:ring-primary focus:border-primary rounded-sm placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage className="text-destructive font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-mono text-xs uppercase tracking-widest">Service Requirements</FormLabel>
            <FormControl>
              <Textarea placeholder="Please describe your vehicle's current condition and the treatments you are interested in..." className="min-h-[120px] bg-secondary/40 border-border/50 focus:ring-primary focus:border-primary rounded-sm resize-none placeholder:text-muted-foreground/50" {...field} />
            </FormControl>
            <FormMessage className="text-destructive font-mono text-[10px]" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-xs font-mono text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[180px] bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-xs rounded-sm transition-all duration-300 shadow-md">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Transmitting...
          </> : <>
            Send Request
            <Send className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  const contactInfo = [{
    icon: MapPin,
    title: "RUNNING MOBILE SERVICE",
    details: "Glenview, Northbrook, Deerfield, Wilmette, Arlington Heights, Chicago North Shore",
    link: null,
    action: null
  }, {
    icon: Phone,
    title: "DIRECT LINE",
    details: "(224) 202-7700",
    link: "tel:+12242027700",
    action: "Call Now"
  }, {
    icon: Mail,
    title: "DIGITAL CONCIERGE",
    details: "jae@bathersautodetailing.com",
    link: "mailto:jae@bathersautodetailing.com",
    action: "Send Email"
  }, {
    icon: Clock,
    title: "OPERATIONAL HOURS",
    details: "By Appointment Only",
    link: null,
    action: null
  }];
  return <section id="reach-the-studio" className="relative py-24 overflow-hidden bg-background dark text-foreground">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary opacity-10 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-secondary opacity-20 blur-[100px]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          <div className="flex flex-col space-y-10">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tighter text-foreground uppercase">
                Reach the <span className="text-primary">running mobile service</span>
              </h2>
              <p className="text-lg text-muted-foreground font-sans max-w-md leading-relaxed">
                Experience the pinnacle of automotive restoration. Our team of specialists is ready to discuss your vehicle's specific needs and aesthetic goals.
              </p>
            </div>

            <div className="space-y-8">
              {contactInfo.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-5 group">
                  <div className="shrink-0 mt-1">
                    <div className="size-12 rounded-sm bg-secondary border border-border/50 flex items-center justify-center group-hover:border-primary/50 transition-all duration-300 shadow-sm">
                      <item.icon className="size-5 text-primary group-hover:scale-110 transition-transform duration-300" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-mono uppercase tracking-widest text-foreground/70 text-sm">{item.title}</h3>
                    <div className="flex flex-col">
                      <span className="text-foreground font-sans font-medium">{item.details}</span>
                      {item.link && <Link to={item.link} className="text-primary text-xs font-mono uppercase tracking-wider mt-1 hover:underline">
                          {item.action}
                        </Link>}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-secondary/40 backdrop-blur-xl rounded-sm border border-border/50 shadow-2xl -z-10" />
            
            <div className="p-6 md:p-8 lg:p-10 rounded-sm bg-card/10 border border-white/5 shadow-inner">
              <div className="mb-8">
                <h3 className="text-2xl font-serif font-bold text-foreground uppercase tracking-tight">Book a Consultation</h3>
                <p className="text-muted-foreground mt-2 font-sans text-sm">
                  Complete the technical brief below. A detailing specialist will review your requirements and provide a tailored proposal.
                </p>
              </div>

              <FormProvider formId="reach_studio_form_get_in_touch" sectionName="reach-the-studio" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true
              }
            }} submitText="Send Request" successMessage="Your brief has been submitted successfully. We will contact you shortly to confirm your running mobile service appointment." errorMessage="Transmission error. Please verify your connection and try again." className="w-full">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
      <Toaster />
    </section>;
}